import { useNavigate } from 'react-router'
import { Helmet } from 'react-helmet'
import { APP } from '@/common/strings'
import { APP_ROUTES } from '@/common/constants'
import { analytics } from '@/common/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { ONBOARDING_COPY } from '@/locales/en'
import { useEffect, lazy, Suspense, type ComponentType } from 'react'
import useUserStore from '@/user/userStore'
import { MobileVerifyForm } from './features/mobile-verify/components/MobileVerifyForm'
import { useMobileVerifyLogic } from './features/mobile-verify/useMobileVerifyLogic'
import { useOnboardingProgress } from './useOnboardingProgress'
import { useLocalFlags } from '@/common/featureFlags/local/useLocalFlags'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import type { PageProps as MobileVerifyV2Props } from './features/mobile-verify-v2/components/MobileVerifyV2'

let LazyMobileVerifyV2:
    | ComponentType<
          MobileVerifyV2Props & {
              onSubmit?: (data: any) => void
              onOtpChange?: (otp: string) => void
              onResend?: () => void
              onBack?: () => void
              onContactSupportClick?: () => void
          }
      >
    | undefined

if (import.meta.env.DEV || import.meta.env.VITE_ENV === 'dev') {
    LazyMobileVerifyV2 = lazy(() =>
        import('./features/mobile-verify-v2/components/MobileVerifyV2').then((m) => ({ default: m.MobileVerifyV2 })),
    )
}

export function MobileVerifyPage() {
    const navigate = useNavigate()
    const { progress } = useOnboardingProgress()
    const { user } = useUserStore()
    const { showOnboardingV2 } = useLocalFlags()
    const { otp, setOtp, resendTimer, resent, error, isVerifying, isSuccess, handleResend, handleSubmit } =
        useMobileVerifyLogic()

    // Guard: redirect if user.mobile is missing
    useEffect(() => {
        if (!user?.mobile) {
            if (window.innerWidth < 640 && !showOnboardingV2) {
                navigate(APP_ROUTES.MOBILE_NUMBER)
                return
            }

            navigate(APP_ROUTES.ABOUT_YOU)
        }
    }, [user?.mobile, navigate, showOnboardingV2])

    // Navigate to survey on success
    useEffect(() => {
        if (!isSuccess) return
        if (showOnboardingV2 && LazyMobileVerifyV2) {
            navigate(APP_ROUTES.SIGNED_UP)
            return
        }
        navigate(APP_ROUTES.SURVEY)
    }, [isSuccess, navigate, showOnboardingV2])

    const helmet = (
        <Helmet>
            <title>
                {ONBOARDING_COPY.MobileVerification.pageTitle} | {APP.title}
            </title>
        </Helmet>
    )

    const handleBack = () => {
        analytics.trackWithDefaults(ANALYTICS_EVENTS.ONBOARDING.VERIFICATION_BACK)
        if (window.innerWidth < 640 && !showOnboardingV2) {
            navigate(APP_ROUTES.MOBILE_NUMBER)
            return
        }

        navigate(APP_ROUTES.ABOUT_YOU)
    }

    if (showOnboardingV2 && LazyMobileVerifyV2) {
        const MobileVerifyV2 = LazyMobileVerifyV2
        return (
            <>
                {helmet}
                <Suspense fallback={<FullScreenLoader dark />}>
                    <MobileVerifyV2
                        mobile={user?.mobile}
                        otp={otp}
                        resendTimer={resendTimer}
                        resent={resent}
                        error={error}
                        verifying={isVerifying}
                        onSubmit={handleSubmit}
                        onOtpChange={setOtp}
                        onResend={handleResend}
                    />
                </Suspense>
            </>
        )
    }

    return (
        <>
            {helmet}
            <MobileVerifyForm
                progress={progress}
                mobile={user?.mobile}
                otp={otp}
                resendTimer={resendTimer}
                resent={resent}
                error={error}
                loading={isVerifying}
                onSubmit={handleSubmit}
                onBack={handleBack}
                onOtpChange={setOtp}
                onResend={handleResend}
            />
        </>
    )
}
